import React, { type FC, Fragment, type MouseEvent, type PropsWithChildren, type ReactNode, useId, useState } from 'react'
import {
    alpha,
    Box,
    ButtonBase,
    buttonBaseClasses,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    ListSubheader,
    Menu,
    MenuItem,
    svgIconClasses,
    type SxProps,
    type Theme,
    useTheme,
} from '@mui/material'

import { assert } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, selectAuthenticatedUsername, Translation } from '../../../domain'

import { DocumentationType, LogoutReason } from '../../../application'

import { DocumentationFilledIcon, LogoutIcon, MenuHamburgerIcon } from '../common'
import { useDocumentationURL } from '../Documentation'
import { useTranslator } from '../Internationalization'
import { AbsoluteRouteOnlyPath, type AbsoluteRoutePath, ForwardRefPermissionedLink, useHasCapabilities, useRouting } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { getIconSizeSx, insetFocusOutline } from '../theme'
import { Brand } from './Brand'
import { getAllRouteOnlyPaths, TOP_NAVIGATION_SECTIONS } from './Config'
import { useNavigationFormatter } from './Hooks'
import { NavigationPermissionedLink } from './Link'
import { useNavigationRouting } from './NavigationRoutingHook'
import { selectedNavigationItemStyle } from './Styles'
import { TopBar } from './TopBar'
import { ChevronDownIcon, CloseIcon } from '../theme/icons'

const DRAWER_ID = 'mobile-navigation-drawer'
const DRAWER_SCREEN_SHARE = '86vw'
const DRAWER_MAX_WIDTH = 300
const MENU_BUTTON_SIZE = 40
const MENU_ICON_SIZE = 24
const ROW_MIN_HEIGHT = 44
const SECTION_HEADER_MIN_HEIGHT = 48
const ROW_ICON_SIZE = 20
const AVATAR_SIZE = 28
const BACKDROP_OPACITY = 0.45

/** Three columns: the menu button, the brand in the middle, and the badge at the end */
const toolbarContentStyle: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    height: '100%',
    px: 2,
}
const barEndStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center', justifySelf: 'end' }
const menuButtonStyle: SxProps<Theme> = {
    justifySelf: 'start',
    width: `${MENU_BUTTON_SIZE}px`,
    height: `${MENU_BUTTON_SIZE}px`,
    color: 'grey.700',
    '&:hover': { color: 'grey.700', backgroundColor: 'grey.200' },
    [`& .${svgIconClasses.root}`]: getIconSizeSx(MENU_ICON_SIZE),
}

/** Browsers without CSS `round()` drop the whole declaration, so they get the unrounded width first */
const drawerPaperStyle: SxProps<Theme> = {
    width: `min(${DRAWER_SCREEN_SHARE}, ${DRAWER_MAX_WIDTH}px)`,
    /** Rounded down to a whole pixel, so the panel's edge stays on the pixel grid on any phone */
    '@supports (width: round(down, 1px, 1px))': { width: `min(round(down, ${DRAWER_SCREEN_SHARE}, 1px), ${DRAWER_MAX_WIDTH}px)` },
    backgroundColor: 'grey.100',
    boxShadow: 4,
}
const backdropStyle: SxProps<Theme> = { backgroundColor: (theme) => alpha(theme.palette.grey[900], BACKDROP_OPACITY) }
const closeButtonStyle: SxProps<Theme> = { alignSelf: 'flex-start' }
const drawerContentStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2, py: 4, px: 3 }
const navStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 }

const rowStyle: SxProps<Theme> = (theme) => ({
    ...theme.typography.body2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 3,
    width: '100%',
    minHeight: `${ROW_MIN_HEIGHT}px`,
    px: 2,
    borderRadius: 1,
    color: 'grey.700',
    textAlign: 'left',
    '&:hover': { backgroundColor: 'grey.200' },
    [`&:focus-visible, &.${buttonBaseClasses.focusVisible}`]: insetFocusOutline(theme),
})
const rowIconStyle: SxProps<Theme> = { width: `${ROW_ICON_SIZE}px`, height: `${ROW_ICON_SIZE}px`, color: 'grey.500' }

const sectionsStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column' }
const sectionHeaderStyle: SxProps<Theme> = { minHeight: `${SECTION_HEADER_MIN_HEIGHT}px`, fontWeight: 500 }
const currentSectionStyle: SxProps<Theme> = { color: 'primary.700', [`& .${svgIconClasses.root}`]: { color: 'primary.700' } }
const sectionLabelStyle: SxProps<Theme> = { flex: 1 }
const chevronStyle: SxProps<Theme> = {
    ...rowIconStyle,
    transition: (theme) => theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
}
const expandedChevronStyle: SxProps<Theme> = { transform: 'rotate(180deg)' }

const pageListStyle: SxProps<Theme> = { listStyle: 'none', m: 0, pt: 1, pb: 2, px: 0 }
const pageLinkStyle: SxProps<Theme> = { pl: 8 }
const currentPageIconStyle: SxProps<Theme> = { [`& .${svgIconClasses.root}`]: { color: 'primary.700' } }
const groupDividerStyle: SxProps<Theme> = { mx: 2, my: 1, borderColor: 'grey.300' }

const accountAreaStyle: SxProps<Theme> = { mt: 'auto', pt: 2, borderTop: 1, borderColor: 'grey.300' }
const accountRowStyle: SxProps<Theme> = { fontWeight: 500 }
const avatarStyle: SxProps<Theme> = (theme) => ({
    ...theme.typography.caption,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    width: `${AVATAR_SIZE}px`,
    height: `${AVATAR_SIZE}px`,
    borderRadius: '50%',
    backgroundColor: 'primary.50',
    color: 'primary.900',
    fontWeight: 600,
})
/** A name the user chose can be any length, so it wraps rather than being cut off */
const accountNameStyle: SxProps<Theme> = { flex: 1, overflowWrap: 'anywhere' }
/** The card opens just above the account row, and the account row's width is set when it opens */
const accountCardStyle: SxProps<Theme> = { mt: -1 }
const accountHeadingStyle: SxProps<Theme> = (theme) => ({
    ...theme.typography.caption,
    fontWeight: 'fontWeightRegular',
    px: 4,
    pt: 2,
    pb: 1.5,
    color: 'grey.500',
    overflowWrap: 'anywhere',
})
/** As on the action menu's destructive items: `700` keeps AA contrast on the hovered item's grey */
const logoutItemStyle: SxProps<Theme> = { color: 'error.700', [`& .${svgIconClasses.root}`]: { color: 'inherit' } }
/** The paper slot's type has no `data-testid`, and a spread property isn't checked against it the way a written one is */
const accountCardPaperTestId = { 'data-testid': 'mobile-navigation-account-card-paper' }

type NavigateProps = Readonly<{ onNavigate: VoidFunction }>

/** A modified click opens the link elsewhere (new tab, window), so the user stays on this page and the drawer stays open */
const isPlainPrimaryClick = (event: MouseEvent): boolean => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

/** A click on the link bubbles up here: `NavigationPermissionedLink` takes no click handler of its own */
const PageItem: FC<NavigateProps & PropsWithChildren> = ({ onNavigate, children }) => (
    <Box
        component="li"
        onClick={(event) => {
            if (isPlainPrimaryClick(event)) {
                onNavigate()
            }
        }}
    >
        {children}
    </Box>
)

const PageLink: FC<Readonly<{ route: AbsoluteRouteOnlyPath }> & NavigateProps> = ({ route, onNavigate }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const Icon = formatter.formatIcon(route)
    const current = routing.isAtRoute(route)

    return (
        <PageItem onNavigate={onNavigate}>
            <NavigationPermissionedLink
                path={route}
                aria-current={current ? 'page' : undefined}
                sx={[rowStyle, pageLinkStyle, current && selectedNavigationItemStyle, current && currentPageIconStyle]}
            >
                <Icon sx={rowIconStyle} />
                {formatter.formatTitle(route)}
            </NavigationPermissionedLink>
        </PageItem>
    )
}

type SectionProps = Readonly<{ section: AbsoluteRouteOnlyPath, current: boolean, expanded: boolean, onToggle: VoidFunction }> & NavigateProps

const Section: FC<SectionProps> = ({ section, current, expanded, onToggle, onNavigate }) => {
    const routing = useNavigationRouting()
    const formatter = useNavigationFormatter()
    const hasCapabilities = useHasCapabilities()
    const theme = useTheme()

    if (!hasCapabilities(section)) {
        return null
    }

    const Icon = formatter.formatIcon(section)
    const pagesId = `mobile-navigation-section-${section}`
    const groups = routing
        .getSectionMenuGroups(section, getAllRouteOnlyPaths())
        .map((group) => group.filter((route) => hasCapabilities(route)))
        .filter((group) => group.length > 0)

    return (
        <Box data-testid="mobile-navigation-section">
            <ButtonBase
                aria-expanded={expanded}
                aria-controls={expanded ? pagesId : undefined}
                onClick={onToggle}
                sx={[rowStyle, sectionHeaderStyle, current && currentSectionStyle]}
                data-testid="mobile-navigation-section-header"
            >
                <Icon sx={rowIconStyle} />
                <Box component="span" sx={sectionLabelStyle}>
                    {formatter.formatTitle(section)}
                </Box>
                <ChevronDownIcon sx={[chevronStyle, expanded && expandedChevronStyle]} />
            </ButtonBase>
            <Collapse in={expanded} timeout={theme.transitions.duration.shorter} unmountOnExit>
                <Box id={pagesId} component="ul" sx={pageListStyle}>
                    {groups.map((group, k) => (
                        <Fragment key={k}>
                            {k > 0 && <Divider component="li" sx={groupDividerStyle} data-testid="mobile-navigation-group-divider" />}
                            {group.map((route) => <PageLink key={route} route={route} onNavigate={onNavigate} />)}
                        </Fragment>
                    ))}
                </Box>
            </Collapse>
        </Box>
    )
}

const NAME_SEPARATORS = /[\s._-]+/

/**
 * Two letters for the account circle: the first letters of the name's first two parts ("jane.doe" → JD), or the first two
 * letters of its only part ("_admin" → AD). `Array.from` splits by character, so a letter outside the BMP stays whole.
 */
const getInitials = (name: string): string => {
    const [first = name, second] = name.split(NAME_SEPARATORS).filter(Boolean)
    const letters = second === undefined ? Array.from(first).slice(0, 2) : [Array.from(first)[0], Array.from(second)[0]]
    return letters.join('').toUpperCase()
}

const AccountMenu: FC<NavigateProps> = ({ onNavigate }) => {
    const translator = useTranslator()
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const hasCapabilities = useHasCapabilities()
    const logoutUsecase = useAppUsecase('logoutUsecase')
    const username = useAppState(selectAuthenticatedUsername)
    assert(username !== null, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'The navigation is only rendered for an authenticated user'))

    const rowId = useId()
    const cardId = useId()
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)
    const documentationHref = useDocumentationURL(DocumentationType.CYBUS_DOCS)
    const SettingsIcon = formatter.formatIcon(AbsoluteRouteOnlyPath.SETTINGS)
    const atSettings = routing.isAtRoute(AbsoluteRouteOnlyPath.SETTINGS)
    const open = anchor !== null

    const closeCard = (): void => setAnchor(null)
    const closeAll = (event: MouseEvent): void => {
        if (isPlainPrimaryClick(event)) {
            closeCard()
            onNavigate()
        }
    }

    return (
        <Box sx={accountAreaStyle}>
            <ButtonBase
                id={rowId}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? cardId : undefined}
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={[rowStyle, accountRowStyle]}
                data-testid="mobile-navigation-account"
            >
                <Box component="span" sx={avatarStyle} aria-hidden>
                    {getInitials(username)}
                </Box>
                <Box component="span" sx={accountNameStyle} data-testid="mobile-navigation-username">
                    {username}
                </Box>
                <ChevronDownIcon sx={[chevronStyle, open && expandedChevronStyle]} />
            </ButtonBase>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={closeCard}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: { sx: [accountCardStyle, { width: anchor?.offsetWidth }], ...accountCardPaperTestId },
                    list: { id: cardId, 'aria-labelledby': rowId },
                }}
                data-testid="mobile-navigation-account-card"
            >
                <ListSubheader role="none" disableSticky sx={accountHeadingStyle}>
                    {translator.formatTranslation(Translation.NAVIGATION_PHONE_SIGNED_IN_AS, { username })}
                </ListSubheader>
                <MenuItem component="a" href={documentationHref} target="_blank" onClick={closeAll} data-testid="mobile-navigation-documentation">
                    <DocumentationFilledIcon sx={rowIconStyle} />
                    {translator.formatTranslation(Translation.DOCUMENTATION)}
                </MenuItem>
                {hasCapabilities(AbsoluteRouteOnlyPath.SETTINGS) && (
                    <MenuItem
                        component={ForwardRefPermissionedLink}
                        path={AbsoluteRouteOnlyPath.SETTINGS}
                        color="inherit"
                        aria-current={atSettings ? 'page' : undefined}
                        selected={atSettings}
                        sx={atSettings ? currentSectionStyle : undefined}
                        onClick={closeAll}
                        data-testid="mobile-navigation-settings"
                    >
                        <SettingsIcon sx={rowIconStyle} />
                        {formatter.formatTitle(AbsoluteRouteOnlyPath.SETTINGS)}
                    </MenuItem>
                )}
                <Divider component="li" data-testid="mobile-navigation-account-divider" />
                <MenuItem
                    onClick={() => {
                        closeCard()
                        onNavigate()
                        logoutUsecase.logout(LogoutReason.MANUAL_LOGOUT)
                    }}
                    sx={logoutItemStyle}
                    data-testid="mobile-navigation-logout"
                >
                    <LogoutIcon sx={rowIconStyle} />
                    {translator.formatTranslation(Translation.LOGOUT)}
                </MenuItem>
            </Menu>
        </Box>
    )
}

type MobileNavigationProps = Readonly<{ deviationsBadge?: ReactNode }>

export const MobileNavigation: FC<MobileNavigationProps> = ({ deviationsBadge }) => {
    const translator = useTranslator()
    const routing = useNavigationRouting()

    const [open, setOpen] = useState(false)
    const [expandedSection, setExpandedSection] = useState<AbsoluteRoutePath | null>(null)

    const currentSection = routing.getCurrentSection()
    const close = (): void => setOpen(false)

    return (
        <>
            <TopBar>
                <Box sx={toolbarContentStyle} data-testid="mobile-navigation-bar">
                    <IconButton
                        variant="ghost"
                        aria-label={translator.formatTranslation(Translation.MENU)}
                        aria-expanded={open}
                        aria-controls={open ? DRAWER_ID : undefined}
                        onClick={() => {
                            setExpandedSection(currentSection)
                            setOpen(true)
                        }}
                        sx={menuButtonStyle}
                        data-testid="mobile-navigation-menu-button"
                    >
                        <MenuHamburgerIcon />
                    </IconButton>
                    <Brand hideNameWhenTight />
                    <Box sx={barEndStyle}>
                        {deviationsBadge}
                    </Box>
                </Box>
            </TopBar>
            <Drawer id={DRAWER_ID} anchor="left" open={open} onClose={close} slotProps={{ paper: { sx: drawerPaperStyle }, backdrop: { sx: backdropStyle } }}>
                <Box sx={drawerContentStyle}>
                    <IconButton
                        variant="ghost"
                        aria-label={translator.formatTranslation(Translation.NAVIGATION_PHONE_MENU_CLOSE)}
                        onClick={close}
                        sx={[menuButtonStyle, closeButtonStyle]}
                        data-testid="mobile-navigation-close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box component="nav" aria-label={translator.formatTranslation(Translation.MENU)} sx={navStyle} data-testid="mobile-navigation-drawer">
                        <Box sx={sectionsStyle}>
                            {TOP_NAVIGATION_SECTIONS.map((section) => (
                                <Section
                                    key={section}
                                    section={section}
                                    current={section === currentSection}
                                    expanded={section === expandedSection}
                                    onToggle={() => setExpandedSection(section === expandedSection ? null : section)}
                                    onNavigate={close}
                                />
                            ))}
                        </Box>
                        <AccountMenu onNavigate={close} />
                    </Box>
                </Box>
            </Drawer>
        </>
    )
}
